import { DraftMissingValue } from 'components/drafts/DraftNotice';
import { fieldName } from 'components/drafts/DraftSaveMode';
import type { Row } from 'components/GridItemList';
import { Children, isValidElement, type ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { t } from 'utils/getTranslations';

/** The text a row's label reads: its message, the first one where the label also holds a history button or a hint. */
function labelText(label: ReactNode): string | null {
    if (typeof label === 'string') {
        return label;
    }
    if (!isValidElement<{ id?: unknown; children?: ReactNode }>(label)) {
        return null;
    }
    if (label.type === FormattedMessage && typeof label.props.id === 'string') {
        return t(label.props.id);
    }
    const [first] = Children.toArray(label.props.children);
    return first === undefined ? null : labelText(first);
}

/**
 * A draft's read rows, each value it was saved without standing as "Missing, required to create", a row hidden when
 * empty included. Matched by label: the form names a missing field by its label, which the read rows share, or
 * which a row labelled otherwise lists in its `formLabels`. Case and a trailing colon aside, since the two designs
 * word a few labels apart only by case, a draft may be read in the other design, and the form names a field without
 * the colon its label may end in. A row that holds a value keeps it.
 */
export const withMissing = (rows: Row[], missingFields: string[] | undefined): Row[] => {
    if (!missingFields?.length) {
        return rows;
    }
    const key = (label: string) => fieldName(label).toLowerCase();
    const missing = new Set(missingFields.map(key));
    return rows.map((row) => {
        const labels = [labelText(row.label), ...(row.formLabels ?? [])];
        const hasValue =
            (typeof row.content === 'string' && row.content.trim() !== '' && row.content !== '-') ||
            !!row.chips?.length;
        return !hasValue && labels.some((label) => label !== null && missing.has(key(label)))
            ? { ...row, content: <DraftMissingValue />, chips: undefined, hidden: false }
            : row;
    });
};
